import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import * as moment from 'moment-timezone';
import { ActivatedRoute } from '@angular/router';
import { isEmpty } from 'lodash';

import { UserService, UserAuth } from '@nrwl/common-lib';

import { ProfileSettingsUpdater } from '../../profile-settings-updater';

export interface EmailFormat {
  label: string;
  value: string;
}

export enum TimeRange {
  FIVE_MINS = '5 min',
  TEN_MINS = '10 min',
  FIFTEEN_MINS = '15 min',
  THIRTY_MINS = '30 min',
  ONE_HOUR = '1 hr'
}

export enum TimeInSeconds {
  FIVE_MINS = 300,
  TEN_MINS = 600,
  FIFTEEN_MINS = 900,
  THIRTY_MINS = 1800,
  ONE_HOUR = 3600
}

@Component({
  selector: 'nrwl-alert-configuration',
  templateUrl: './alert-configuration.component.html',
  styleUrls: ['./alert-configuration.component.scss']
})
export class AlertConfigurationComponent
  extends ProfileSettingsUpdater
  implements OnInit, OnDestroy {
  @Input() alertPreferenceSubject: BehaviorSubject<any>; //eslint-disable-line
  userName: string;
  alertPreferenceData: any; //eslint-disable-line
  defaultCriticalAlert: string;
  defaultWarningAlert: string;
  frequencyCriticalAlerts = 0;
  frequencyWarningAlerts = 0;
  emailFormatOptions: EmailFormat[] = [
    {
      label: 'HTML',
      value: 'html'
    },
    {
      label: 'Plain text',
      value: 'plain-text'
    }
  ];
  timeZoneOptions: string[] = moment.tz.names();
  alertType = 'General';
  emailFormatSelected: string;
  emailTimeZoneSelected: string;
  performanceEmailEnabled = false;
  criticalAlerts: string[];
  warningAlerts: string[];
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(userService: UserService, activatedRoute: ActivatedRoute, userAuth: UserAuth) {
    super(userService, activatedRoute, userAuth);
  }

  ngOnInit() {
    this.setAllDefaultValues();
    this.getUserNotificationData();

    this.alertPreferenceSubject.pipe(takeUntil(this.unsubscribe)).subscribe(requestData => {
      if (!isEmpty(requestData)) {
        this.alertPreferenceData = requestData;
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribeUpdates();
  }

  //eslint-disable-next-line
  getUserNotificationData(): any {
    this.alertPreferenceSubject.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.loadNotificationPreference(data);
    });
  }

  //eslint-disable-next-line
  loadPreferences(userPref: any): void {
    this.emailFormatSelected = userPref.alertsPreference.emailFormat;
  }

  //eslint-disable-next-line
  onUserPrefsChange(event: any, inputName: string): void {
    const currentUser = this.getQueryUserName();

    const value = this.getValueFromDifferentInput(event, inputName);
    const formatedUpdate = this.userService.updatePreference(inputName, value);

    this.userService
      .updateWebUIPreference$(formatedUpdate, currentUser)
      .pipe(
        takeUntil(this.unsubscribe),
        tap(() => {
          switch (inputName) {
            case 'emailTimeZoneSelected':
              this.emailTimeZoneSelected = event.value;
              break;
            case 'emailFormatSelected':
              this.emailFormatSelected = event.value;
              break;
            default:
              break;
          }

          this.alertPreferenceData.timeZone = this.emailTimeZoneSelected;
          this.alertPreferenceData.emailFormat = this.emailFormatSelected;
          this.updateUserAlertsPreferences(this.alertPreferenceData);
        })
      )
      .subscribe();
  }

  //eslint-disable-next-line
  getValueFromDifferentInput(event: any, name: string): any {
    //eslint-disable-line
    switch (name) {
      case 'enableSendImmediate':
      case 'enableDailyDigest': {
        return event.checked;
      }
      case 'emailFormatSelected':
      case 'emailTimeZoneSelected':
      case 'digestEmailTimeSelected': {
        return event.value;
      }
      default:
        throw new Error(`Input name (${name}) not implemented`);
    }
  }

  //eslint-disable-next-line
  enablePerformance(event: any, value: any) {
    this.performanceEmailEnabled = event.checked;
    this.alertPreferenceData.performanceAlertConfiguration.isEmailEnabled = this.performanceEmailEnabled;
    this.updateUserAlertsPreferences(this.alertPreferenceData);
  }

  //eslint-disable-next-line
  tabChanged(event: any): void {
    this.alertType = event.tab.textLabel;
  }

  isGigatestDeployment(): boolean {
    return this.userService.isGigatestUser;
  }

  //eslint-disable-next-line
  onUserAlertChange(event: any, value: any) {
    if (value === 'criticalAlert') {
      switch (event.value) {
        case TimeRange.FIVE_MINS: {
          this.frequencyCriticalAlerts = TimeInSeconds.FIVE_MINS;
          break;
        }
        case TimeRange.TEN_MINS: {
          this.frequencyCriticalAlerts = TimeInSeconds.TEN_MINS;
          break;
        }
        case TimeRange.FIFTEEN_MINS: {
          this.frequencyCriticalAlerts = TimeInSeconds.FIFTEEN_MINS;
          break;
        }
        case TimeRange.THIRTY_MINS: {
          this.frequencyCriticalAlerts = TimeInSeconds.THIRTY_MINS;
          break;
        }
        case TimeRange.ONE_HOUR: {
          this.frequencyCriticalAlerts = TimeInSeconds.ONE_HOUR;
          break;
        }
      }
    } else if (value === 'warningAlert') {
      switch (event.value) {
        case TimeRange.FIVE_MINS: {
          this.frequencyWarningAlerts = TimeInSeconds.FIVE_MINS;
          break;
        }
        case TimeRange.TEN_MINS: {
          this.frequencyWarningAlerts = TimeInSeconds.TEN_MINS;
          break;
        }
        case TimeRange.FIFTEEN_MINS: {
          this.frequencyWarningAlerts = TimeInSeconds.FIFTEEN_MINS;
          break;
        }
        case TimeRange.THIRTY_MINS: {
          this.frequencyWarningAlerts = TimeInSeconds.THIRTY_MINS;
          break;
        }
        case TimeRange.ONE_HOUR: {
          this.frequencyWarningAlerts = TimeInSeconds.ONE_HOUR;
          break;
        }
      }
    }

    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertFrequency.frequencyCriticalAlerts = this.frequencyCriticalAlerts;
    this.alertPreferenceData.performanceAlertConfiguration.performanceAlertFrequency.frequencyWarningAlerts = this.frequencyWarningAlerts;
    this.updateUserAlertsPreferences(this.alertPreferenceData);
  }

  private setDefaultAlertValue(data) {
    const criticaAlert = data[0];
    const warningAlert = data[1];

    if (criticaAlert.name === 'critical') {
      switch (criticaAlert.value) {
        case TimeInSeconds.FIVE_MINS: {
          this.defaultCriticalAlert = TimeRange.FIVE_MINS;
          break;
        }
        case TimeInSeconds.TEN_MINS: {
          this.defaultCriticalAlert = TimeRange.TEN_MINS;
          break;
        }
        case TimeInSeconds.FIFTEEN_MINS: {
          this.defaultCriticalAlert = TimeRange.FIFTEEN_MINS;
          break;
        }
        case TimeInSeconds.THIRTY_MINS: {
          this.defaultCriticalAlert = TimeRange.THIRTY_MINS;
          break;
        }
        case TimeInSeconds.ONE_HOUR: {
          this.defaultCriticalAlert = TimeRange.ONE_HOUR;
          break;
        }
      }
    }
    if (warningAlert.name === 'warning') {
      switch (warningAlert.value) {
        case TimeInSeconds.FIVE_MINS: {
          this.defaultWarningAlert = TimeRange.FIVE_MINS;
          break;
        }
        case TimeInSeconds.TEN_MINS: {
          this.defaultWarningAlert = TimeRange.TEN_MINS;
          break;
        }
        case TimeInSeconds.FIFTEEN_MINS: {
          this.defaultWarningAlert = TimeRange.FIFTEEN_MINS;
          break;
        }
        case TimeInSeconds.THIRTY_MINS: {
          this.defaultWarningAlert = TimeRange.THIRTY_MINS;
          break;
        }
        case TimeInSeconds.ONE_HOUR: {
          this.defaultWarningAlert = TimeRange.ONE_HOUR;
          break;
        }
      }
    }
  }

  private loadNotificationPreference(alertsPreference) {
    if (!isEmpty(alertsPreference)) {
      const performanceAlertConfiguration = alertsPreference.performanceAlertConfiguration;
      this.emailTimeZoneSelected = alertsPreference.timeZone;
      this.emailFormatSelected = alertsPreference.emailFormat;
      this.performanceEmailEnabled = performanceAlertConfiguration.isEmailEnabled;
      this.frequencyCriticalAlerts =
        performanceAlertConfiguration.performanceAlertFrequency.frequencyCriticalAlerts;
      this.frequencyWarningAlerts =
        performanceAlertConfiguration.performanceAlertFrequency.frequencyWarningAlerts;
      this.setDefaultAlertValue([
        {
          name: 'critical',
          value: this.frequencyCriticalAlerts
        },
        {
          name: 'warning',
          value: this.frequencyWarningAlerts
        }
      ]);
    }
  }

  private setAllDefaultValues() {
    this.criticalAlerts = ['5 min', '10 min', '15 min', '30 min', '1 hr'];
    this.warningAlerts = ['5 min', '10 min', '15 min', '30 min', '1 hr'];
  }
}
